import { useHandler } from "@chatbotgang/etude/react/useHandler";
import FormControlLabel from "@mui/material/FormControlLabel";
import Radio from "@mui/material/Radio";
import RadioGroup, { type RadioGroupProps } from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import type { SxProps } from "@mui/material/styles";
import type { muiTheme } from "@polifonia/theme";
import { theme } from "@polifonia/theme";
import { getRouteApi, Navigate, useNavigate } from "@tanstack/react-router";
import { useSnackbar } from "notistack";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { Badge } from "@/components/Badge";
import { Button } from "@/components/Button";
import { BarLoading } from "@/components/Loading/BarLoading";
import { Typography } from "@/components/Typography";
import { useEnableGoogleAdsIntegrationMilestone1 } from "@/features/google-ads/useEnableGoogleAdsIntegration";
import type { InterludeTypes } from "@/interlude";
import { interlude } from "@/interlude";
import { useAuthorizeWithGoogle } from "@/pages/applications/pages/googleAdsIntegration/api/useAuthorizeWithGoogle";
import { PageLayout } from "@/pages/components/PageLayout";
import { PageTitle } from "@/pages/components/PageTitle";

const routeApi = getRouteApi(
  "/applications/google-ads-integration/oauth/callback",
);

const INTEGRATION_PAGE_PATH = "/applications/google-ads-integration";

const styles = {
  section: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
    maxWidth: "512px",
  },
  customerOption: {
    alignItems: "flex-start",
    margin: "0",
    padding: "12px",
    borderRadius: "8px",
    backgroundColor: theme.colors.staticBgContainer,
  },
  customerLabel: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    paddingTop: "8px",
  },
} satisfies Record<string, SxProps<typeof muiTheme>>;

const formatCustomerId = (customerId: string): string =>
  customerId.replace(/^(\d{3})(\d{3})(\d{4})$/, "$1-$2-$3");

interface ResultMessageProps {
  title: string;
  description: string;
}

const ResultMessage: React.FC<ResultMessageProps> = function ResultMessage({
  title,
  description,
}) {
  const { t } = useTranslation();
  const authorizeWithGoogleMutation = useAuthorizeWithGoogle();

  const handleAuthorizeAgain = useHandler(() => {
    authorizeWithGoogleMutation.mutate();
  });

  return (
    <Stack sx={styles.section}>
      <Stack gap="8px">
        <Typography
          variant="h3"
          fontWeight="medium"
          color={theme.colors.staticFgTitle}
        >
          {title}
        </Typography>
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {description}
        </Typography>
      </Stack>
      <Stack direction="row" gap="12px">
        <Button
          variant="primary"
          onClick={handleAuthorizeAgain}
          loading={authorizeWithGoogleMutation.isPending}
          disabled={authorizeWithGoogleMutation.isPending}
        >
          {t("applications.googleAds.oauth.authorizeAgain", "Authorize again")}
        </Button>
        <Button variant="plain" href={INTEGRATION_PAGE_PATH}>
          {t(
            "applications.googleAds.oauth.backToIntegration",
            "Back to Google Ads",
          )}
        </Button>
      </Stack>
    </Stack>
  );
};

const OAuthNotCompletedMessage: React.FC = function OAuthNotCompletedMessage() {
  const { t } = useTranslation();
  return (
    <ResultMessage
      title={t(
        "applications.googleAds.oauth.failed.title",
        "Google Ads was not connected",
      )}
      description={t(
        "applications.googleAds.oauth.failed.description",
        "Authorization did not finish. Authorize again to retry.",
      )}
    />
  );
};

interface SelectCustomerStepProps {
  oauthCredentialId: number;
  customers: Array<InterludeTypes["GoogleAdsAccessibleCustomer"]>;
}

const SelectCustomerStep: React.FC<SelectCustomerStepProps> =
  function SelectCustomerStep({ oauthCredentialId, customers }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();
    const [selectedCustomerId, setSelectedCustomerId] = useState<string | null>(
      customers.length === 1 ? (customers[0]?.customerId ?? null) : null,
    );

    const registerManagerAccountMutation =
      interlude.googleAds.useRegisterManagerAccount(undefined, {
        onSuccess: () => {
          enqueueSnackbar(
            t("applications.googleAds.registerManagerAccount.success"),
            { variant: "success" },
          );
          navigate({ to: INTEGRATION_PAGE_PATH, replace: true });
        },
      });

    const handleChange = useHandler<RadioGroupProps["onChange"]>(
      (_event, value) => {
        setSelectedCustomerId(value);
      },
    );

    const handleConnect = useHandler(() => {
      if (selectedCustomerId === null) return;
      registerManagerAccountMutation.mutate({
        customerId: selectedCustomerId,
        oauthCredentialId,
      });
    });

    if (customers.length === 0) {
      return (
        <ResultMessage
          title={t(
            "applications.googleAds.oauth.noCustomers.title",
            "No Google Ads accounts found",
          )}
          description={t(
            "applications.googleAds.oauth.noCustomers.description",
            "This Google account has no Google Ads accounts. Authorize again with a Google account that can access your Google Ads manager account.",
          )}
        />
      );
    }

    return (
      <Stack sx={styles.section}>
        <Stack gap="8px">
          <Typography
            variant="h3"
            fontWeight="medium"
            color={theme.colors.staticFgTitle}
          >
            {t(
              "applications.googleAds.oauth.selectCustomer.title",
              "Choose a Google Ads account",
            )}
          </Typography>
          <Typography variant="note" color={theme.colors.staticFgNote}>
            {t(
              "applications.googleAds.oauth.selectCustomer.description",
              "Choose the manager account (MCC) to connect. Ad accounts under it are added automatically.",
            )}
          </Typography>
        </Stack>
        <RadioGroup value={selectedCustomerId ?? ""} onChange={handleChange}>
          <Stack gap="8px">
            {customers.map((customer) => (
              <FormControlLabel
                key={customer.customerId}
                value={customer.customerId}
                disabled={registerManagerAccountMutation.isPending}
                control={<Radio size="small" />}
                sx={styles.customerOption}
                label={
                  <Stack sx={styles.customerLabel}>
                    <Stack direction="row" gap="8px" alignItems="center">
                      <Typography
                        variant="body"
                        color={theme.colors.staticFgTitle}
                      >
                        {customer.name}
                      </Typography>
                      {customer.isManager ? (
                        <Badge variant="plain">
                          {t(
                            "applications.googleAds.oauth.selectCustomer.managerBadge",
                            "Manager account",
                          )}
                        </Badge>
                      ) : null}
                    </Stack>
                    <Typography
                      variant="note"
                      color={theme.colors.staticFgNote}
                    >
                      {formatCustomerId(customer.customerId)}
                    </Typography>
                  </Stack>
                }
              />
            ))}
          </Stack>
        </RadioGroup>
        <Stack direction="row" gap="12px">
          <Button
            variant="primary"
            onClick={handleConnect}
            loading={registerManagerAccountMutation.isPending}
            disabled={
              selectedCustomerId === null ||
              registerManagerAccountMutation.isPending
            }
          >
            {t("common.connect")}
          </Button>
          <Button
            variant="plain"
            href={INTEGRATION_PAGE_PATH}
            disabled={registerManagerAccountMutation.isPending}
          >
            {t("common.cancel")}
          </Button>
        </Stack>
      </Stack>
    );
  };

interface OAuthCallbackContentProps {
  code: string;
  state: string;
}

const OAuthCallbackContent: React.FC<OAuthCallbackContentProps> =
  function OAuthCallbackContent({ code, state }) {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    const navigate = useNavigate();

    const managerAccountQuery = interlude.googleAds.useGetManagerAccount({
      queries: { checkAccess: false },
    });
    // An org already connected through OAuth came here from the expired-grant
    // prompt, so the new credential replaces the old one instead of
    // registering a manager account.
    const isReauthorizing = managerAccountQuery.data?.authType === "oauth";

    const reauthorizeMutation =
      interlude.googleAds.useReauthorizeManagerAccount(undefined, {
        onSuccess: () => {
          enqueueSnackbar(
            t(
              "applications.googleAds.oauth.reauthorizeSuccess",
              "Google Ads authorized again",
            ),
            { variant: "success" },
          );
          navigate({ to: INTEGRATION_PAGE_PATH, replace: true });
        },
      });

    const exchangeOAuthCodeMutation = interlude.googleAds.useExchangeOAuthCode(
      undefined,
      {
        onSuccess: (data) => {
          if (!isReauthorizing) return;
          reauthorizeMutation.mutate({
            oauthCredentialId: data.oauthCredentialId,
          });
        },
      },
    );

    // Google's code is single-use, so a second exchange (StrictMode re-running
    // the effect) would fail with an invalid state.
    const hasExchangedRef = useRef(false);
    const isManagerAccountPending = managerAccountQuery.isPending;
    const exchangeOAuthCode = exchangeOAuthCodeMutation.mutate;
    useEffect(() => {
      if (hasExchangedRef.current || isManagerAccountPending) return;
      hasExchangedRef.current = true;
      exchangeOAuthCode({ code, state });
    }, [code, state, isManagerAccountPending, exchangeOAuthCode]);

    if (exchangeOAuthCodeMutation.isError || reauthorizeMutation.isError) {
      return <OAuthNotCompletedMessage />;
    }

    if (exchangeOAuthCodeMutation.isSuccess && !isReauthorizing) {
      return (
        <SelectCustomerStep
          oauthCredentialId={exchangeOAuthCodeMutation.data.oauthCredentialId}
          customers={exchangeOAuthCodeMutation.data.customers}
        />
      );
    }

    return (
      <Stack gap="8px">
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {t(
            "applications.googleAds.oauth.processing",
            "Finishing Google authorization…",
          )}
        </Typography>
        <BarLoading />
      </Stack>
    );
  };

const GoogleAdsOAuthCallbackPageInternal: React.FC =
  function GoogleAdsOAuthCallbackPageInternal() {
    const { t } = useTranslation();
    const search = routeApi.useSearch();

    if (search.error === "access_denied") {
      return (
        <ResultMessage
          title={t(
            "applications.googleAds.oauth.cancelled.title",
            "Authorization cancelled",
          )}
          description={t(
            "applications.googleAds.oauth.cancelled.description",
            "Google Ads was not connected. Authorize again whenever you're ready.",
          )}
        />
      );
    }

    if (search.error || !search.code || !search.state) {
      return <OAuthNotCompletedMessage />;
    }

    return <OAuthCallbackContent code={search.code} state={search.state} />;
  };

export const GoogleAdsOAuthCallbackPage: React.FC =
  function GoogleAdsOAuthCallbackPage() {
    const { t } = useTranslation();
    const enableGoogleAdsIntegration =
      useEnableGoogleAdsIntegrationMilestone1();

    if (!enableGoogleAdsIntegration) {
      return <Navigate to="/applications" replace />;
    }

    return (
      <PageLayout>
        <PageTitle>{t("applications.googleAds.title")}</PageTitle>
        <GoogleAdsOAuthCallbackPageInternal />
      </PageLayout>
    );
  };
